import {
    IUserCondensed,
    IUserProfileMinimal,
    IWhitelistUser,
    USER_ROLE,
} from "./user"

// ========================================
// Trend Summary (One-to-One relationship)
// ========================================

export type TrendStatus = "active" | "archived";

export enum AccountLifecycleStatus {
    ACTIVE = "ACTIVE",
    ARCHIVED = "ARCHIVED",
    DELETED = "DELETED",
}

export interface ITrendSummary {
    id: string; // UUID
    date: string; // ISO 8601 Datetime
    overview: string;
    key_developments: string[];
    impact: string;
    status: TrendStatus;
    account_id?: string;
    industry_id?: string;
}

export interface IActiveAccount {
    account_id: number,
    total_score: number,
    account_name: string,
    highest_insight_score: number
}

export type AccountUserPriorityStatus = 'HIGH' | 'LOW' | 'NONE';

export interface IAccountTag {
    id: number;
    /** Present on tag CRUD responses; omitted on slim list tags. */
    account_id?: string;
    text: string;
    /** Present on tag CRUD responses; omitted on slim list tags. */
    created_at?: string;
    created_by_user: {
        id: string;
        name: string | null;
        full_name: string | null;
    } | null;
}

/** Numeric IDs are retained only for existing offline tutorial fixtures. Live query IDs are UUID strings. */
export type AccountId = string | number;

export interface IAccountCondensed {
    campaign_id?: string,
    city?: string | null,
    state?: string | null,
    country?: string | null,
    domain: string, 
    name: string,
    status?: AccountLifecycleStatus;
    deletion_state?: "none" | "deletion_pending" | "deletion_failed";
    client_id: string,
    description: string | null,
    logo_image_url: string | null,
    creation_time: Date,
    last_update: Date,
    /** Newest TIER_1 insight date; null when the account has none. */
    latest_insight_published_date: Date | string | null,
    id: AccountId,
    industry: string | null,
    headcount?: number | null,
    primary_user: IUserProfileMinimal | null,
    secondary_users: IUserProfileMinimal[],
    primary_whitelisted_user_id?: string | null,
    primary_whitelisted_user?: IWhitelistUser | null,
    secondary_whitelisted_users?: IWhitelistUser[],
    num_account_insights: number,
    num_contacts: number,
    num_actions: number,
    has_new_insight: boolean,
    managed_by: IUserProfileMinimal | null,
    managed_by_whitelist_id?: string | null,
    managed_by_whitelisted_user?: IWhitelistUser | null,
    tags?: IAccountTag[],
    priority_user_status?: AccountUserPriorityStatus
    crm_sync_enabled?: boolean
    crm_missing?: boolean
    crm_linked?: boolean
    crm_review_pending?: boolean
    crm_auto_push_account_point_of_view?: boolean
    crm_auto_push_contacts?: boolean
    crm_auto_push_completed_activities?: boolean
    crm_auto_push_scheduled_activities?: boolean
    crm_auto_push_scheduled_min_priority?: number
    crm_import_contacts?: boolean
    crm_import_scheduled_completions?: boolean
}

// export interface IAccountContacts {
//     contacts: IContactIntelligence[]
// }

export interface IAccountPointOfView {
    text: string;
    supporting_insight_ids: number[];
    generated_at: string;
    campaign_id: string;
    campaign_updated_at: string;
    taxonomy_revision: number;
    prompt_version: string;
    evidence_window_days: number;
}

export interface IAccountFull {
    point_of_view?: IAccountPointOfView | null;
    domain: string,
    name: string,
    refreshable: boolean,
    status?: AccountLifecycleStatus;
    archived_at?: string | null;
    aliases: string[],
    client_id: string,
    campaign_id: string,
    industry_id: string | null,
    linkedin_company_url: string | null,
    specialties: string[],
    description: string | null,
    logo_image_url: string | null,
    creation_time: Date,
    last_update: Date,
    trend_summary: ITrendSummary | null,
    trends: TrendReadWithAccountInsights[],
    last_insight_update: Date | null,
    id: number,
    industry: string | null,
    client: IClient,
    primary_user: IUserCondensed | null,
    secondary_users: IUserCondensed[],
    num_account_insights: number,
    num_contacts: number,
    num_insights: number,
    insights: IInsight[] | null,
    completed_insights: IInsight[] | null,
    signal_bundles?: IAccountSignalBundle[] | null,
    priority_playbooks?: IAccountPriorityPlaybook[] | null,
    contact_intelligence: IContactIntelligence[] | null,
    managed_by: string | null,
    contact_searching: boolean,
    headcount?: number | null,
    employees: string | null,
    revenue: string | null,
    phone_number: string | null,
    address: string | null,
    city: string | null,
    state: string | null,
    zip: string | null,
    country: string | null,
    priority_user_status?: AccountUserPriorityStatus
    crm_sync_enabled?: boolean
    crm_missing?: boolean
    crm_linked?: boolean
    crm_review_pending?: boolean
    crm_auto_push_account_point_of_view?: boolean
    crm_auto_push_contacts?: boolean
    crm_auto_push_completed_activities?: boolean
    crm_auto_push_scheduled_activities?: boolean
    crm_auto_push_scheduled_min_priority?: number
    crm_import_contacts?: boolean
    crm_import_scheduled_completions?: boolean
}

export interface ICampaign {
    id: string;
    client_id: string;
    name: string;
    is_default: boolean;
    created_at: string;
    updated_at: string;
}

export interface ICampaignAccountMoveFailure {
    account_id: string;
    reason: string;
    message: string;
}

export interface ICampaignAccountMoveResult {
    succeeded: string[];
    failed: ICampaignAccountMoveFailure[];
}

export interface IClient {
    domain: string,
    name: string,
    id: number
}

export enum AccountInsightTier {
    TIER_1 = "TIER_1",
    TIER_2 = "TIER_2",
}

export interface IAccountSignalBundle {
    id: number;
    account_id: string;
    tier: AccountInsightTier;
    primary_signal_id: string;
    group_title: string;
    group_synopsis: string | null;
    event_count: number;
    updated_at: string;
    insight_ids: number[];
}

export type PriorityPlaybookSourceType = "tier_0_combo" | "priority_rule";

export interface IAccountPriorityPlaybook {
    id: number;
    account_id: string;
    source_type: PriorityPlaybookSourceType;
    playbook_key: string;
    taxonomy_name: string;
    signal_formula: string[];
    priority: InsightPriority;
    priority_rationale: string;
    group_title: string;
    group_synopsis: string | null;
    example_outreach_angle: string | null;
    event_count: number;
    updated_at: string;
    insight_ids: number[];
}

export enum InsightPriority {
    HIGH = "HIGH",
    MEDIUM = "MEDIUM",
    LOW = "LOW",
}

export enum ContextStrength {
    WEAK = "WEAK",
    STRONG = "STRONG",
    NEAR_CERTAIN = "NEAR_CERTAIN",
}

export interface KeyQuote {
    quote: string;
    source_url?: string | null;
    source_title?: string | null;
}

export interface InsightSource {
    url: string;
    title?: string | null;
}

export interface SupportingContextTag {
    context_id: string;
    score: number;
    strength: ContextStrength;
    use_in_messaging?: string | null;
}

export interface TierTaxonomySignal {
    id: string;
    label: string;
}

export interface TierTaxonomy {
    action_signals?: TierTaxonomySignal[];
    context_signals?: TierTaxonomySignal[];
    [key: string]: unknown;
}

export type CampaignTierSignalTaxonomy = TierTaxonomy | null;

export interface ClientTierSignalTaxonomyRead {
    client_id: string;
    tier_signal_taxonomy: TierTaxonomy | null;
}

export interface IInsight {
    insight_category: INSIGHT_CATEGORY 
    link_text: string | null,
    link_url: string | null,
    link_image: string | null,
    date: Date | null,
    type: INSIGHT_TYPE,
    notes: string[] | null,
    synopsis: string | null,
    /** Brief line for lists/hotlist; preferred over link_text when set. */
    short_summary?: string | null,
    subtype: ACCOUNT_INSIGHT_SUBTYPE | null,
    account_id: string | null,
    account_name: string | null,
    id: number,
    is_new_for_user: boolean,
    insight_interaction_status: IInsightInteractionStatus,
    recency_score: number,
    relevance_score: number,
    overall_score: number,
    industry: IIndustryCondensed | null,
    is_about_competitor: boolean,
    competitor_confidence: number | null,
    matched_competitors: string[] | null,
    tier?: AccountInsightTier | null,
    primary_signal_id?: string | null,
    secondary_tier_ids?: string[],
    supports_signal_ids?: string[],
    tier_question?: string | null,
    action_type?: string | null,
    priority?: InsightPriority | null,
    priority_rationale?: string | null,
    key_quotes?: KeyQuote[],
    sources?: InsightSource[],
    supporting_context?: SupportingContextTag[],
}

export interface IContactIntelligence {
    name: string,
    title: string | null,
    linkedin_url: string | null,
    /** Professional skills from provider; may be present before enrichment. */
    skills?: string[] | null,
    summary: string[] | null;
    my_commonalities: string[] | null,
    relevance_score: number | null,
    overall_score?: number | null,
    email: string | null,
    email_status: ContactEmailStatus,
    direct_phone_number: string | null,
    mobile_phone_number: string | null, 
    corporate_phone_number: string | null,
    profile_picture_url: string | null,
    id: number,
    company_name: string | null,
    city: string | null,
    state: string | null,
    country: string | null,
    insight_interaction_status: IInsightInteractionStatus,
    account: IAccountCondensed,
    enrichment_status: ContactEnrichmentStatus,
    /** Linked to a CRM record, so email/phone came from the client's own CRM. */
    crm_linked?: boolean,
    /** Human-readable buying-group label, e.g. "Finance", "IT Leadership". */
    decision_making_unit?: string | null,
}

export interface IContactLimits {
    current_contact_count: number,
    max_contacts_per_account: number,
    contacts_remaining: number
}

export interface ISocialPromptList {
    author: {
        id: string,
        name: string | null,
        full_name: string
    } | null,
    social_prompts: ISocialPrompt[]
}

/** Shape of `SocialPromptSeries` / series read DTOs from database_service. */
export enum SocialPromptSeriesKind {
    STANDALONE = "STANDALONE",
    SEQUENCE = "SEQUENCE",
    VARIATION_SET = "VARIATION_SET",
}

export interface ISocialPromptSeriesContactAssigned {
    id: number;
    name?: string | null;
    title?: string | null;
}

/** `GET /social_prompts/series/insights/...` — one series with nested prompts and status. */
export interface ISocialPromptSeriesReadWithStatus {
    id: string;
    kind: SocialPromptSeriesKind;
    title?: string | null;
    total_steps: number;
    account_id?: string | null;
    user_id?: string | null;
    contact_id?: number | null;
    contact_assigned?: ISocialPromptSeriesContactAssigned | null;
    account_insight_id?: number | null;
    industry_insight_id?: number | null;
    insight_item_id?: string | null;
    asset_id?: number | null;
    generation_request_id?: string | null;
    created_at?: string;
    prompts: ISocialPrompt[];
}

/** `GET /social_prompts/series/{series_id}` — series with ordered prompts (no per-prompt status required). */
export interface ISocialPromptSeriesReadWithPrompts {
    id: string;
    kind: SocialPromptSeriesKind;
    title?: string | null;
    total_steps: number;
    account_id?: string | null;
    user_id?: string | null;
    contact_id?: number | null;
    contact_assigned?: ISocialPromptSeriesContactAssigned | null;
    account_insight_id?: number | null;
    industry_insight_id?: number | null;
    insight_item_id?: string | null;
    asset_id?: number | null;
    generation_request_id?: string | null;
    created_at?: string;
    prompts: ISocialPrompt[];
}

export enum MESSAGE_FORMAT {
    TEXT = "TEXT",
    MARKDOWN = "MARKDOWN",
}

export interface ISocialPrompt {
    content_type: SOCIAL_CONTENT_TYPE | null,
    subject: string | null,
    message: string | null,
    message_format: MESSAGE_FORMAT,
    /** Legacy; prefer `step_number` from series API. */
    number_in_series?: number | null,
    /** 1-based position within the owning series. */
    step_number?: number | null,
    series_id?: string | null,
    total_steps?: number | null,
    series_kind?: SocialPromptSeriesKind | null,
    contact_id?: number | null,
    account_insight_id?: number | null,
    industry_insight_id?: number | null,
    asset_id?: number | null,
    id: number,
    created_at?: string,
    interaction_status: IInsightInteractionStatus
}

export interface IAvailableSPVoice {
    key: VOICE_KEY,
    label: string,
    description: string,
    is_active: boolean
}

export interface ISocialPromptPreferences {
    email: ISocialPromptPreference | null,
    call: ISocialPromptPreference | null,
    linkedin_post: ISocialPromptPreference | null,
    linkedin_dm: ISocialPromptPreference | null,
    blog: ISocialPromptPreference | null
}

export interface ISocialPromptPreference {
    voice_key: VOICE_KEY | null,
    context: string | null
}

export interface IIndustryCondensed {
    name: string,
    description: string | null,
    last_update: string | null,
    refreshable: boolean,
    id: number
}

export interface IIndustry {
    name: string,
    description: string | null,
    last_update: string | null,
    refreshable: boolean,
    id: number,
    industry_insights: IInsight[],
    trend_summary: ITrendSummary | null,
    trends: TrendReadWithIndustryInsights[],
}

export interface IClientType {
    lifecycle?: "active" | "deletion_pending" | "deletion_failed",
    domain: string,
    name: string,
    id: string,
    campaign_description: string | null,
    desired_job_functions: string[] | null,
    desired_job_levels: string[] | null
}



export enum ACCOUNT_INSIGHT_SUBTYPE {
    ACCOUNT = "ACCOUNT",
    MARKET = "MARKET"
}

export enum INSIGHT_STATUS {
    NOT_SEEN = "NOT_SEEN",
    SEEN = "SEEN",
    COMPLETED = "COMPLETED",
    DISMISSED = "DISMISSED"  
}

export interface IInsightInteractionStatus {
    favorite: boolean,
    index: number | null,
    notes: string | null,
    insight_status: INSIGHT_STATUS
}

export enum INSIGHT_CATEGORY {
    ACCOUNT = "ACCOUNT",
    INDUSTRY = "INDUSTRY",
    ASSET = "ASSET"
}

export enum INSIGHT_TYPE {
    NEWS = "NEWS",
    EARNINGS_CALL = "EARNINGS_CALL",
    SPEAKING_ENGAGEMENT = "SPEAKING_ENGAGEMENT",
    PARTICIPATION_IN_AN_EVENT = "PARTICIPATION_IN_AN_EVENT",
    LOCATION_EXPANSION = "LOCATION_EXPANSION",
    HIRING = "HIRING",
    LAYOFFS = "LAYOFFS",
    PARTNERSHIPS = "PARTNERSHIPS",
    LEADERSHIP_CHANGES = "LEADERSHIP_CHANGES",
    INTENT = "INTENT",
    LINKEDIN_POST = "LINKEDIN_POST",
    AWARD = "AWARD",
    MERGER = "MERGER",
    ACQUISITION = "ACQUISITION",
    OTHER = "OTHER",
}

export enum SOCIAL_CONTENT_TYPE {
    EMAIL = "EMAIL",
    CALL = "CALL",
    LINKEDIN_POST = "LINKEDIN_POST",
    LINKEDIN_DM = "LINKEDIN_DM",
    BLOG = "BLOG"
}

export enum VOICE_KEY {
    INTENTICO_RECOMMENDED = "INTENTICO_RECOMMENDED",
    CHALLENGER = "CHALLENGER",
    VISIONARY = "VISIONARY",
    TECHNICAL = "TECHNICAL"
}

export enum ContactEnrichmentStatus {
    NOT_ENRICHED = "NOT_ENRICHED",
    ENRICHED_SUCCESS = "ENRICHED_SUCCESS",
    ENRICHED_NO_DATA = "ENRICHED_NO_DATA"
}

export type ContactEmailStatus = string | null;

// ========================================
// Trend API Response Models
// ========================================

export interface TrendReadBase {
    id: string;
    account_id: string | null;
    industry_id: string | null;
    title: string;
    description: string;
    status: TrendStatus;
    created_at: string;
    last_updated_at: string;
    title_generated_at: string | null;
    insight_count_at_title_gen: number | null;
}

export interface AccountInsightRead {
    id: number;
    account_id: string;
    type: INSIGHT_TYPE;
    subtype: ACCOUNT_INSIGHT_SUBTYPE;
    title: string | null;
    summary: string | null;
    date: string | null;
    url: string | null;
    source: string | null;
    created_at: string;
}

export interface IndustryInsightRead {
    id: number;
    industry_id: string;
    type: INSIGHT_TYPE;
    title: string | null;
    summary: string | null;
    date: string | null;
    url: string | null;
    source: string | null;
    created_at: string;
    is_about_competitor: boolean;
    competitor_confidence: number | null;
    matched_competitors: string[] | null;
}

export interface AccountInsightInTrend {
    insight: AccountInsightRead;
    similarity_score: number;
    primary: boolean;
}

export interface IndustryInsightInTrend {
    insight: IndustryInsightRead;
    similarity_score: number;
    primary: boolean;
}

export interface TrendReadWithAccountInsights extends TrendReadBase {
    account_insights: AccountInsightInTrend[];
}

export interface TrendReadWithIndustryInsights extends TrendReadBase {
    industry_insights: IndustryInsightInTrend[];
}

// ========================================
// Account plan – candidate recommended action (plan_snapshot JSON)
// ========================================

export interface IAccountPlan {
    items: ICandidateAction[];
    account_id: string;
    start_date: string;
    end_date: string;
}

export enum CANDIDATE_ACTION_TYPE {
    INSIGHT_EMAIL = "INSIGHT_EMAIL",
    INSIGHT_CALL = "INSIGHT_CALL",
    INSIGHT_LINKEDIN_POST = "INSIGHT_LINKEDIN_POST",
    INSIGHT_BLOG = "INSIGHT_BLOG",
    INSIGHT_LINKEDIN_DM = "INSIGHT_LINKEDIN_DM",
    CONTACT_ENRICH = "CONTACT_ENRICH",
    CONTACT_EMAIL = "CONTACT_EMAIL",
    CONTACT_CALL = "CONTACT_CALL",
    CONTACT_LINKEDIN_DM = "CONTACT_LINKEDIN_DM",
    CONTACT_LINKEDIN_ENGAGE = "CONTACT_LINKEDIN_ENGAGE",
}

/** Lightweight reference to another step in a prompt-series cadence (orchestrator `series_context`). */
export interface SeriesStepRef {
    delivery_summary?: import("../Api/smartlead").SmartleadDeliverySummary | null;
    action_id: string;
    sequence_position: number;
    suggested_date: string | null;
    action_status: string | null;
    action_type: CANDIDATE_ACTION_TYPE | string;
    channel: string | null;
    /** Populated on sibling refs when the orchestrator has a prompt but no schedule date yet. */
    prompt_id?: number | "NONE" | null;
    /** Set when the step has been completed; ISO 8601 datetime from the orchestrator. */
    completed_at?: string | null;
}

export type SeriesKind = "STANDALONE" | "SEQUENCE" | "VARIATION_SET";

/** Series navigation context attached to a suggested action by the orchestrator. */
export interface SeriesContext {
    series_id: string;
    series_kind: SeriesKind | string;
    sequence_position: number;
    previous_steps: SeriesStepRef[];
    next_steps: SeriesStepRef[];
    has_incomplete_prerequisites: boolean;
    earliest_incomplete_action_id: string | null;
}

/** Typed subset of `ICandidateAction.metadata` for series / contact fields. */
export interface ICandidateActionMetadata {
    sequence_position?: number;
    series_id?: string;
    series_kind?: SeriesKind | string;
    to_contact_id?: number | string;
    contact_id?: number | string;
}

/** A single candidate recommended action for an account. */
export interface ICandidateAction {
    delivery_summary?: import("../Api/smartlead").SmartleadDeliverySummary | null;
    working_set_status?: "active" | "finishing" | null;
    action_id: string;
    /** Lifecycle status; omitted by older API responses. */
    action_status?: string | null;
    action_type: CANDIDATE_ACTION_TYPE;
    account_id: string;
    /**
     * Urgency score in \[0, 1\]; higher = more urgent.
     * UI bands: high ≥ 0.87, medium 0.7–&lt;0.87, lowest tier &lt; 0.7 (labeled **Standard** / **Standard Priority** in the app).
     */
    priority: number;
    source_insight_id: number | null;
    source_insight_type: string | null;
    source_contact_id: number | null;
    /** null or the string "NONE" both mean no existing prompt — the action intent is to generate one. */
    prompt_id: number | "NONE" | null;
    suggested_at: string; // ISO 8601 datetime
    suggested_date: string | null; // ISO date (YYYY-MM-DD); when null, use suggested_at date
    channel: string | null;
    metadata: Record<string, unknown> | null;
    /** Populated by orchestrator for multi-step cadence actions; omitted on legacy rows. */
    series_context?: SeriesContext | null;
    is_approved?: boolean;
    approved_at?: string | null;
    approved_by_user_id?: string | null;
}

// ========================================
// Account history (rep_activity) – aligns with user events
// ========================================
/** Single activity row from rep_activity. event_type and payload match user events; payload and metadata are optional JSON strings (stored as-is). */
export interface IAccountHistoryItem {
    delivery_summary?: import("../Api/smartlead").SmartleadDeliverySummary | null;
    event_id: string;
    client_id: string;
    user_id: string;
    account_id: string | null;
    contact_id: number | null;
    industry_id: string | null;
    event_type: string;
    payload: string | null;
    metadata: string | null;
    occurred_at: string;
    created_at: string;
    /** Server-resolved display fields (orchestrator). May be null when the resolver could not find the record. */
    account_name?: string | null;
    account_domain?: string | null;
    contact_name?: string | null;
    user_full_name?: string | null;
    sales_leader_id?: string | null;
    sales_leader_name?: string | null;
}

/** Response for GET /v1/accounts/{account_id}/history: past actions/touches for account. */
export interface IAccountHistory {
    items: IAccountHistoryItem[];
}

/** Paginated feed section of the activity view response. */
export interface IActivityFeedResponse {
    items: IAccountHistoryItem[];
    total: number;
    limit: number;
    offset: number;
    user_id: string;
    /** Role-resolved user roster for filter popovers (same shape as daily/weekly lists). */
    scope?: IListScope;
}

/** Per-cycle-week suggested action counts returned in the metrics section. */
export interface ISuggestedCountByWeek {
    week_start: string;
    account_action_count: number;
    thought_leadership_count: number;
}

/** Suggested counts by coarse category in the metrics section. */
export interface ISuggestedCountByCategory {
    insight: number;
    contact: number;
    thought_leadership: number;
}

/** Outreach channel suggested counts (account actions only) in the metrics section. */
export interface ISuggestedCountByChannel {
    email: number;
    call: number;
    linkedin_post: number;
    linkedin_dm: number;
    linkedin_connection: number;
    linkedin_engagement?: number;
    blog: number;
}

/** Suggested action counts by priority band in the metrics section. */
export interface ISuggestedPriorityByBand {
    standard: number;
    medium: number;
    high: number;
    unclassified: number;
}

/** Priority score stats and band counts for suggested actions in `[since, until)`. */
export interface ISuggestedPrioritySummary {
    average?: number;
    minimum?: number;
    maximum?: number;
    by_band: ISuggestedPriorityByBand;
}

/** Completed/dismissed history counts for one bucket in the metrics section. */
export interface IHistoryCountBucket {
    completed: number;
    dismissed: number;
}

/** History event counts by category in the metrics section. */
export interface IHistoryCountByCategory {
    insight: IHistoryCountBucket;
    contact: IHistoryCountBucket;
    outreach: IHistoryCountBucket;
}

/** Client-side aggregate of suggested counts for a date range. */
export type ISuggestedCountBreakdown = {
    total: number;
    accountActions: number;
    thoughtLeadership: number;
};

/** Per-bucket metrics for Hotlist Action history deck tiles (full date range). */
export type IHistoryBucketMetrics = {
    confirmedEmailSends?: number;
    queuedEmailCompletions?: number;
    actions: {
        suggested: number;
        completed: number;
        dismissed: number;
        byChannel: ISuggestedCountByChannel;
        prioritySummary?: ISuggestedPrioritySummary;
    };
    insights: {
        suggested: number;
        completed: number;
        dismissed: number;
    };
    contacts: {
        suggested: number;
        completed: number;
        dismissed: number;
    };
    thoughtLeadershipSuggested: number;
    totalSuggested: number;
};

/** Aggregate data from the activity view metrics section. */
export interface IActivityMetricsResponse {
    user_id: string;
    /** Total scheduled actions in `[since, until)`. */
    suggested_count?: number | null;
    /** Per Monday cycle-week breakdown. */
    suggested_count_by_week?: ISuggestedCountByWeek[];
    /** Category breakdown for the full range. */
    suggested_count_by_category?: ISuggestedCountByCategory;
    /** Outreach channel breakdown for account actions. */
    suggested_count_by_channel?: Partial<ISuggestedCountByChannel> | null;
    /** Suggested-action priority band breakdown. */
    suggested_priority_summary?: ISuggestedPrioritySummary;
    /** Authoritative completed/dismissed counts for the full range. */
    history_count_by_category?: IHistoryCountByCategory;
    confirmed_email_sent_count?: number | null;
    queued_email_completed_count?: number | null;
}

/** Query parameters for GET /v1/activity/export (CSV download). */
export interface IActivityExportParams {
    client_id?: string;
    campaign_id?: string;
    all_campaigns?: boolean;
    since?: string;
    until?: string;
    event_types?: string[];
    account_ids?: string[];
    user_ids?: string[];
    include_thought_leadership?: boolean;
}

/** Query parameters for GET /v1/weekly_lists/export (CSV download). */
export interface IWeeklyListsExportParams {
    client_id?: string;
    user_id: string;
    week_start: string;
    approved_only?: boolean;
    filter_user_ids?: string[];
    filter_whitelist_ids?: string[];
    filter_sales_leader_ids?: string[];
    filter_sales_leader_whitelist_ids?: string[];
    filter_unassigned_sales_leader?: boolean;
    campaign_id?: string;
    all_campaigns?: boolean;
}


// ========================================
// Hotlist enrichment + scope (orchestrator)
// ========================================

/** Slim user reference returned by orchestrator enrichment / scope payloads. */
export interface IUserRef {
    id: string;
    full_name?: string | null;
    /** Present on `scope.users` so the frontend can derive the rep filter list without an extra Intentico call. */
    role?: USER_ROLE;
}

/** Sales leader → assigned reps mapping returned in `scope.sales_leader_teams`. */
export interface ISalesLeaderTeamRef {
    leader_id: string;
    user_ids: string[];
}

/** Per-account enrichment block — covers everything DailyActionsTable + rep filter need from an account. */
export interface IAccountEnrichment {
    id: string;
    name: string;
    domain?: string | null;
    industry?: string | null;
    logo_image_url?: string | null;
    primary_user_id?: string | null;
    primary_user?: IUserRef | null;
    secondary_users?: IUserRef[];
    primary_whitelisted_user_id?: string | null;
    primary_whitelisted_user?: IWhitelistUser | null;
    secondary_whitelisted_users?: IWhitelistUser[];
    managed_by?: IUserRef | null;
    managed_by_whitelist_id?: string | null;
    managed_by_whitelisted_user?: IWhitelistUser | null;
}

export interface IListEnrichment {
    accounts: IAccountEnrichment[];
}

/** Role-resolved scope for the rep filter popover and TL assignee labels. */
export interface IListScope {
    users: IUserRef[];
    whitelisted_users?: IWhitelistUser[];
    sales_leader_teams: ISalesLeaderTeamRef[];
    /** Active identities with primary or manager ownership on an authorized Hotlist account. */
    hotlist_user_ids?: string[];
    /** Pending identities with a primary assignment on an authorized Hotlist account. */
    hotlist_whitelist_ids?: string[];
}

export interface IDailyActionsList {
    working_set?: IWorkingSetSummary | null;
    items: ICandidateAction[];
    thought_leadership?: IThoughtLeadershipPlan[];
    date: string;
    enrichment?: IListEnrichment;
    scope?: IListScope;
}

// ========================================
// Thought Leadership weekly plan
// ========================================

export interface IThoughtLeadershipAnchorInsight {
    insight_id: number;
    title: string | null;
    short_summary?: string | null;
    source_url: string | null;
    synopsis: string | null;
    insight_type: string | null;
    insight_date: string | null;
    link_image: string | null;
    overall_score: number | null;
}

export interface IThoughtLeadershipEntry {
    trend_id: string;
    suggested_date?: string | null;
    industry_name: string | null;
    title: string | null;
    description: string | null;
    overall_score: number | null;
    anchor_insight: IThoughtLeadershipAnchorInsight | null;
    status: string;
    is_approved: boolean;
    approved_at: string | null;
    approved_by_user_id: string | null;
    /** Optional prebuilt social prompt id; null/undefined means none yet — show Generate. */
    prompt_id?: number | null;
    /** Channel for the linked prompt; defaults to LinkedIn post when omitted. */
    content_type?: SOCIAL_CONTENT_TYPE | null;
}

export interface IThoughtLeadershipPlan {
    client_id: string;
    user_id: string;
    plan_epoch: string;
    plan_committed_at: string;
    selection_mode: string;
    cycle_length_days: number;
    entries: IThoughtLeadershipEntry[];
}

export interface IApproveThoughtLeadershipRequest {
    is_approved: boolean;
    send_email_to_rep?: boolean;
}

export interface IApproveThoughtLeadershipResponse {
    status: "ok" | "skipped";
    user_id: string;
    trend_id: string;
    updated: boolean;
    entry: IThoughtLeadershipEntry;
}

export interface IWeeklyActionsList {
    working_set?: IWorkingSetSummary | null;
    days: IDailyActionsList[];
    week_start: string;
    user_id: string;
    /**
     * One plan per rep visible to the queried user. For SALES_REP this is 0 or 1 element;
     * for SALES_LEADER it is one per assigned rep; for CLIENT_ADMIN/ADMIN one per rep in tenant.
     */
    thought_leadership?: IThoughtLeadershipPlan[];
    enrichment?: IListEnrichment;
    scope?: IListScope;
}

export interface IApproveActionRequest {
    account_id: string;
    action_type: string;
    source_insight_id: number | null;
    prompt_id: number | null;
    contact_id: number | null;
    channel: string | null;
    is_approved: boolean;
    send_email_to_rep: boolean;
}

export interface IWorkingSetSummary {
    pending_ranking_count?: number;
    active_count: number;
    assigned_count: number;
    preparing_count: number;
    finishing_count: number;
    degraded: boolean;
    initializing?: boolean;
}
